/* Tổng đài Sales — hàng đợi gọi, cuộc gọi đến / nhỡ, nhật ký cuộc gọi. Dùng chung khách, hồ sơ và phiếu kết quả với màn Khách tiềm năng */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button, Empty, Segmented, Select, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  AlarmClock, CalendarCheck2, Contact, Clock3, Coffee, Headset, MessageCircle, Phone, PhoneCall, PhoneIncoming, PhoneMissed, PhoneOutgoing, Play,
} from 'lucide-react';
import Layout from '../../Layout';
import CreateCustomerModal from '../../components/Customer/CreateCustomerModal';
import type { ChannelCustomerItem } from '../../api/ChannelCustomer';
import { getCustomerTags, type CustomerTagItem } from '../../api/CustomerTag';
import { mixGetV1 } from '../../services/mixin';
import useAuthStore from '../../store/authStore';
import { fetchCareStatuses, type CareStatusItem } from '../RoomChat/chatLabels';
import LeadDrawer, { type LeadAction } from './lead/LeadDrawer';
import IncomingCall, { type Ring } from './lead/IncomingCall';
import {
  MAX_RETRY, MISSED, MISSED_MIN, codeLabel, dueText, dur, isConnected, mangOf, maskPhone, mmss, shortTag, staffOf, tagOf, textOn, uid, when,
} from './lead/logic';
import {
  KIND_META, QUEUE_KINDS, buildQueue, callKpi, callLog, missedCalls, talkText, type QItem, type QueueKind,
} from './lead/calls';
import { useLeadStore } from './lead/store';
import { useCallStore, type UnknownCall } from './lead/callStore';
import { enrichDemo, isDemoId } from './lead/demo';
import { currentMe } from './lead/roles';

/* eslint-disable @typescript-eslint/no-explicit-any */
const itemsOf = (res: any): any[] => (Array.isArray(res?.data?.items) ? res.data.items : Array.isArray(res?.data) ? res.data : []);
/* eslint-enable @typescript-eslint/no-explicit-any */
const nameOf = (r: ChannelCustomerItem) => (r.display_name || r.fullname || 'Khách').trim();

const Kpi = ({ icon, label, value, sub, tone = 'teal', onClick, active }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'teal' | 'red' | 'green' | 'amber' | 'slate'; onClick?: () => void; active?: boolean;
}) => {
  const TONE = {
    teal: 'bg-[#e8f4f7] text-[#2c6b7f] dark:bg-[#3e91ab]/20 dark:text-[#7cc3d6]',
    red: 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-500/15 dark:text-green-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
    slate: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  }[tone];
  const Box = onClick ? 'button' : 'div';
  return (
    <Box
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={`flex min-w-0 items-start gap-2.5 rounded-xl border bg-white p-3 text-left dark:bg-gray-800 ${active ? 'border-[#3e91ab] ring-1 ring-[#3e91ab]' : 'border-gray-200 dark:border-gray-700'} ${onClick ? 'transition hover:border-[#3e91ab]' : ''}`}
    >
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${TONE}`}>{icon}</span>
      <span className="min-w-0">
        <span className="block truncate text-[12px] text-gray-500 dark:text-gray-400">{label}</span>
        <span className={`block text-[20px] font-bold leading-tight tabular-nums ${tone === 'red' ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>{value}</span>
        {sub && <span className="block text-[11.5px] leading-snug text-gray-400">{sub}</span>}
      </span>
    </Box>
  );
};

const TagChip = ({ r, tags }: { r: ChannelCustomerItem; tags: CustomerTagItem[] }) => {
  const l = useLeadStore((s) => s.byId[r.id]);
  const tag = tagOf(r, l, tags);
  if (!tag) return null;
  return (
    <Tooltip title={tag.name}>
      <span className="shrink-0 rounded-full px-2 py-px text-[11px] font-bold" style={{ background: tag.color || '#64748b', color: textOn(tag.color) }}>{shortTag(tag.name)}</span>
    </Tooltip>
  );
};

type MissedFilter = 'open' | 'all';
interface MissRow { key: string; at: number; phone: string; r?: ChannelCustomerItem; back?: { at: number; code?: string; by: string }; unknown?: UnknownCall }

const TeleCallCenter: React.FC = () => {
  const { isSuperAdmin, hasPermission } = useAuthStore();
  const canManage = isSuperAdmin || hasPermission('telesales.assign') || hasPermission('telesales.config');
  const canWrite = isSuperAdmin || hasPermission('telesales.edit');
  const me = useMemo(currentMe, []);
  const leads = useLeadStore((s) => s.byId);
  const demo = useLeadStore((s) => s.demo);
  const addTouch = useLeadStore((s) => s.addTouch);
  const { status, setStatus, unknown, addUnknown, doneUnknown } = useCallStore();

  const [now, setNow] = useState(Date.now());
  const [tags, setTags] = useState<CustomerTagItem[]>([]);
  const [careStatuses, setCareStatuses] = useState<CareStatusItem[]>([]);
  const [teleStaffs, setTeleStaffs] = useState<{ value: number; label: string }[]>([]);
  // Telesales xem hàng đợi của mình; quản lý chọn xem theo từng Telesales
  const [staffId, setStaffId] = useState<number | undefined>(canManage ? undefined : me.staffId ?? undefined);
  const [api, setApi] = useState<ChannelCustomerItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('queue');
  const [kind, setKind] = useState<QueueKind | 'late' | null>(null);
  const [missFilter, setMissFilter] = useState<MissedFilter>('open');
  const [logDays, setLogDays] = useState<1 | 7>(1);
  const [selected, setSelected] = useState<{ rec: ChannelCustomerItem; action?: LeadAction; seq: number } | null>(null);
  const [inCall, setInCall] = useState(false);
  const [ring, setRing] = useState<Ring | null>(null);
  const [createFor, setCreateFor] = useState<UnknownCall | null>(null);
  const [drawerWide, setDrawerWide] = useState(false);

  useEffect(() => {
    getCustomerTags({ sort: ['id,asc'] }).then((res) => { if (res.status && res.data) setTags(res.data.items); }).catch(() => undefined);
    fetchCareStatuses().then(setCareStatuses).catch(() => undefined);
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!canManage) return;
    mixGetV1('/api/v1/be/staffs', { type_user: 1, limit: 200, type_department_id: 3 })
      .then((res) => setTeleStaffs(itemsOf(res).filter((s) => s?.id).map((s) => ({ value: Number(s.id), label: s.fullname || s.name || `#${s.id}` }))))
      .catch(() => undefined);
  }, [canManage]);
  // Chỉ đọc danh sách khách (GET); kết quả cuộc gọi lưu trên máy như màn Khách tiềm năng
  useEffect(() => {
    let alive = true;
    setLoading(true);
    mixGetV1('/api/v1/be/customers', { contact_staff_id: staffId || undefined, limit: 100 })
      .then((res) => { if (alive && res?.status) setApi(itemsOf(res)); })
      .catch(() => undefined)
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [staffId]);

  /* ---------- Khách trong phạm vi: khách mẫu + khách từ API, theo Telesales đang xem ---------- */
  const rows = useMemo(() => {
    const demoRows = demo ? enrichDemo(demo.customers, tags, careStatuses) : [];
    const seen = new Set<number>();
    return [...demoRows, ...api].filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return !staffId || staffOf(r, leads[r.id])?.id === staffId;
    });
  }, [demo, tags, careStatuses, api, staffId, leads]);

  const queue = useMemo(() => buildQueue(rows, leads, tags, now), [rows, leads, tags, now]);
  const kpi = useMemo(() => callKpi(rows, leads, now), [rows, leads, now]);
  const late = queue.filter((q) => q.late).length;
  const kindCount = useMemo(() => Object.fromEntries(QUEUE_KINDS.map((k) => [k, queue.filter((q) => q.step.kind === k).length])), [queue]);
  const shown = kind === 'late' ? queue.filter((q) => q.late) : kind ? queue.filter((q) => q.step.kind === kind) : queue;

  const missRows = useMemo<MissRow[]>(() => {
    const from = dayjs(now).subtract(6, 'day').startOf('day').valueOf();
    const known: MissRow[] = missedCalls(rows, leads, from).map(({ r, t, back }) => ({ key: t.id, at: t.at, phone: r.phone || '', r, back }));
    // Số lạ chỉ hiện ở góc nhìn chung (chưa biết thuộc Telesales nào)
    const strangers: MissRow[] = staffId ? [] : unknown.filter((u) => u.missed && u.at >= from).map((u) => ({
      key: u.id, at: u.at, phone: u.phone, unknown: u, back: u.doneAt ? { at: u.doneAt, by: u.doneBy || '' } : undefined,
    }));
    return [...known, ...strangers].sort((a, b) => b.at - a.at);
  }, [rows, leads, unknown, staffId, now]);
  const missOpen = missRows.filter((x) => !x.back).length;

  const logRows = useMemo(() => {
    const from = dayjs(now).subtract(logDays - 1, 'day').startOf('day').valueOf();
    const known = callLog(rows, leads, from).map(({ r, t }) => ({ key: t.id, at: t.at, r, t, phone: r.phone || '' }));
    const strangers = staffId ? [] : unknown.filter((u) => u.at >= from).map((u) => ({
      key: u.id, at: u.at, r: undefined, phone: u.phone,
      t: { id: u.id, at: u.at, kind: 'call' as const, dir: 'in' as const, dur: 0, code: u.missed ? MISSED : undefined, text: u.missed ? '' : u.doneAt ? 'Số lạ — đã tạo hồ sơ khách' : 'Số lạ — chưa tạo hồ sơ khách', by: u.missed ? 'Tổng đài' : u.by || u.doneBy || '—' },
    }));
    return [...known, ...strangers].sort((a, b) => b.at - a.at);
  }, [rows, leads, unknown, staffId, logDays, now]);

  /* ---------- Mở hồ sơ / gọi ---------- */
  const open = useCallback((r: ChannelCustomerItem, action?: LeadAction) => {
    if (inCall && selected?.rec.id !== r.id) {
      message.warning('Đang có cuộc gọi chưa lưu kết quả — lưu kết quả trước khi mở khách khác.');
      return false;
    }
    setSelected((s) => ({ rec: r, action, seq: (s?.seq ?? 0) + 1 }));
    return true;
  }, [inCall, selected?.rec.id]);
  const callable = (q: QItem) => !!q.r.phone && leads[q.r.id]?.kenh !== 'Chỉ nhắn tin';
  const callNext = () => {
    const idx = queue.findIndex((q) => q.r.id === selected?.rec.id);
    const next = [...queue.slice(idx + 1), ...queue.slice(0, Math.max(idx, 0))].find((q) => q.r.id !== selected?.rec.id && callable(q));
    if (!next) { message.info('Hàng đợi không còn khách cần gọi'); return; }
    // Gọi từ "Lưu và gọi tiếp" ngay sau khi lưu (cờ đang gọi chưa kịp tắt) → không chặn như mở khách thường
    setSelected((s) => ({ rec: next.r, action: 'call', seq: (s?.seq ?? 0) + 1 }));
  };

  /* ---------- Cuộc gọi đến (mô phỏng cho tới khi nối tổng đài) ---------- */
  const missed = (rg: Ring) => {
    if (rg.r) addTouch(rg.r.id, { kind: 'call', dir: 'in', dur: 0, code: MISSED, by: 'Tổng đài', at: rg.at });
    else addUnknown({ at: rg.at, phone: rg.phone, missed: true });
  };
  const simulateIncoming = () => {
    if (ring) return;
    const pool = rows.filter((r) => r.phone);
    const stranger = !pool.length || Math.random() < 0.25;
    const r = stranger ? undefined : pool[Math.floor(Math.random() * pool.length)];
    const rg: Ring = { id: uid(), at: Date.now(), phone: r?.phone || `09${String(Math.floor(Math.random() * 1e8)).padStart(8, '0')}`, r };
    // Đang nghỉ / đang bận cuộc khác → tổng đài ghi nhỡ ngay
    if (status === 'break' || inCall) {
      missed(rg);
      message.warning(`Cuộc gọi nhỡ từ ${r ? nameOf(r) : `số lạ ${maskPhone(rg.phone)}`} — ${inCall ? 'bạn đang trong cuộc gọi khác' : 'bạn đang tạm nghỉ'}`);
      return;
    }
    setRing(rg);
  };
  const answer = () => {
    if (!ring) return;
    if (ring.r) {
      open(ring.r, 'answer');
    } else {
      // Số lạ: nghe máy rồi tạo hồ sơ để các lần sau nhận diện được
      setCreateFor(addUnknown({ at: ring.at, phone: ring.phone, missed: false, by: me.name }));
      message.info(`Đang nghe số lạ ${maskPhone(ring.phone)} — tạo hồ sơ khách để ghi kết quả cuộc gọi`);
    }
    setRing(null);
  };
  const miss = (reason: 'decline' | 'timeout') => {
    if (!ring) return;
    missed(ring);
    message.warning(reason === 'timeout' ? 'Không nghe máy — đã chuyển vào Cuộc nhỡ' : 'Đã từ chối — chuyển vào Cuộc nhỡ');
    setRing(null);
  };

  /* ---------- Bảng ---------- */
  const docked = !!selected;
  const custCell = (r: ChannelCustomerItem, action?: LeadAction) => (
    <button type="button" onClick={() => open(r, action)} className="block min-w-0 max-w-full text-left">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-semibold text-[#3e91ab] hover:underline">{nameOf(r)}</span>
        {isDemoId(r.id) && <span className="shrink-0 rounded bg-amber-100 px-1 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">Mẫu</span>}
        <TagChip r={r} tags={tags} />
      </span>
      <span className="block truncate text-[12px] text-gray-400">{r.customer_code || `KH-${r.id}`}{r.phone ? ` · ${maskPhone(r.phone)}` : ' · chưa có SĐT'}</span>
    </button>
  );

  const queueCols: ColumnsType<QItem> = [
    {
      title: 'Việc', key: 'kind', width: 150,
      render: (_, q) => (
        <div>
          <Tag color={KIND_META[q.step.kind].color} className="!m-0">{KIND_META[q.step.kind].label}</Tag>
          {q.step.kind !== 'new' && q.step.kind !== 'missed' && <div className="mt-0.5 truncate text-[11.5px] text-gray-400">{q.step.label}</div>}
        </div>
      ),
    },
    { title: 'Khách hàng', key: 'cus', render: (_, q) => custCell(q.r) },
    ...(docked ? [] : [{ title: 'Mảng', key: 'mang', width: 100, render: (_: unknown, q: QItem) => mangOf(q.r, leads[q.r.id]) || <span className="text-gray-400">—</span> }]),
    ...(docked || staffId ? [] : [{ title: 'Telesales', key: 'staff', width: 140, render: (_: unknown, q: QItem) => staffOf(q.r, leads[q.r.id])?.name || <span className="text-orange-500">Chưa phân</span> }]),
    {
      title: <Tooltip title={`Khách mới gọi trong 15 phút · cuộc nhỡ gọi lại trong ${MISSED_MIN} phút · gọi lại sau 2 giờ`}>Hạn</Tooltip>, key: 'due', width: 130,
      render: (_, q) => {
        if (!q.step.due) return '—';
        const d = dueText(q.step.due, now);
        return <span className={`font-semibold ${d.late ? 'text-red-600' : 'text-gray-700 dark:text-gray-200'}`}>{d.text}</span>;
      },
    },
    {
      title: <Tooltip title={`Số lần gọi đi liên tiếp chưa liên lạc được (tối đa ${MAX_RETRY})`}>Lần thử</Tooltip>, key: 'tries', width: 80,
      render: (_, q) => (q.tries ? <span className={q.tries >= MAX_RETRY - 1 ? 'font-semibold text-orange-500' : ''}>{q.tries} / {MAX_RETRY}</span> : <span className="text-gray-400">—</span>),
    },
    ...(docked ? [] : [{
      title: 'Lần chạm gần nhất', key: 'last', width: 190,
      render: (_: unknown, q: QItem) => (q.last
        ? <span className="text-[12.5px] text-gray-600 dark:text-gray-300">{when(q.last.at, now)}{q.last.code ? ` · ${q.last.code}` : ''}</span>
        : <span className="text-gray-400">Chưa chạm</span>),
    }]),
    {
      title: '', key: 'act', width: docked ? 60 : 150, align: 'right',
      render: (_, q) => {
        const onlyMsg = leads[q.r.id]?.kenh === 'Chỉ nhắn tin';
        return (
          <div className="flex justify-end gap-1.5">
            {onlyMsg ? (
              <Tooltip title="Khách chỉ muốn nhắn tin"><Button size="small" icon={<MessageCircle size={13} />} onClick={() => open(q.r, 'msg')}>{docked ? null : 'Zalo'}</Button></Tooltip>
            ) : (
              <Tooltip title={!q.r.phone ? 'Chưa có số điện thoại' : ''}>
                <Button size="small" type="primary" disabled={!q.r.phone} icon={<Phone size={13} />} onClick={() => open(q.r, 'call')} className="!bg-[#3e91ab] disabled:!bg-gray-200">{docked ? null : 'Gọi'}</Button>
              </Tooltip>
            )}
            {!docked && !onlyMsg && <Tooltip title="Nhắn Zalo"><Button size="small" icon={<MessageCircle size={13} />} onClick={() => open(q.r, 'msg')} aria-label="Nhắn Zalo" /></Tooltip>}
          </div>
        );
      },
    },
  ];

  const missCols: ColumnsType<MissRow> = [
    { title: 'Lúc nhỡ', key: 'at', width: 120, render: (_, x) => when(x.at, now) },
    {
      title: 'Khách', key: 'cus',
      render: (_, x) => (x.r ? custCell(x.r) : (
        <div><span className="font-semibold text-gray-800 dark:text-gray-100">Số lạ</span><div className="text-[12px] tabular-nums text-gray-400">{maskPhone(x.phone)} · chưa có hồ sơ</div></div>
      )),
    },
    {
      title: 'Tình trạng', key: 'st', width: 220,
      render: (_, x) => (x.back ? (
        <span className="text-[12.5px] text-green-600 dark:text-green-400">
          {x.unknown ? 'Đã xử lý' : 'Đã gọi lại'} {dayjs(x.back.at).format('HH:mm')}{x.back.code ? ` · ${x.back.code}` : ''}{x.back.by ? ` · ${x.back.by}` : ''}
        </span>
      ) : (
        <span className={`text-[12.5px] font-semibold ${now - x.at > MISSED_MIN * 60_000 ? 'text-red-600' : 'text-orange-500'}`}>Chưa gọi lại · chờ {dur(now - x.at)}</span>
      )),
    },
    {
      title: '', key: 'act', width: 200, align: 'right',
      render: (_, x) => {
        if (x.r) return !x.back && <Button size="small" type="primary" icon={<PhoneCall size={13} />} onClick={() => open(x.r!, 'call')} className="!bg-[#3e91ab]">Gọi lại</Button>;
        if (x.back || !x.unknown) return null;
        return (
          <div className="flex justify-end gap-1.5">
            <Button size="small" onClick={() => { doneUnknown(x.unknown!.id, me.name); message.success('Đã đánh dấu xử lý'); }}>Đã xử lý</Button>
            <Button size="small" type="primary" className="!bg-[#3e91ab]" onClick={() => setCreateFor(x.unknown!)}>Tạo khách</Button>
          </div>
        );
      },
    },
  ];

  type LogRow = (typeof logRows)[number];
  const logCols: ColumnsType<LogRow> = [
    { title: 'Thời điểm', key: 'at', width: 130, render: (_, x) => when(x.at, now) },
    {
      title: 'Hướng', key: 'dir', width: 110,
      render: (_, x) => {
        const isMiss = x.t.code === MISSED;
        const Icon = isMiss ? PhoneMissed : x.t.dir === 'in' ? PhoneIncoming : PhoneOutgoing;
        return <span className={`inline-flex items-center gap-1.5 ${isMiss ? 'text-red-500' : 'text-gray-600 dark:text-gray-300'}`}><Icon size={14} />{isMiss ? 'Nhỡ' : x.t.dir === 'in' ? 'Gọi đến' : 'Gọi đi'}</span>;
      },
    },
    { title: 'Khách', key: 'cus', render: (_, x) => (x.r ? custCell(x.r) : <span>Số lạ · <span className="tabular-nums text-gray-400">{maskPhone(x.phone)}</span></span>) },
    { title: 'Thời lượng', key: 'dur', width: 90, render: (_, x) => (x.t.dur ? <span className="tabular-nums">{mmss(x.t.dur)}</span> : <span className="text-gray-400">—</span>) },
    {
      title: 'Kết quả', key: 'code', width: 220,
      render: (_, x) => (x.t.code
        ? <span><b className={isConnected(x.t.code) ? 'text-green-600 dark:text-green-400' : x.t.code === MISSED ? 'text-red-500' : ''}>{x.t.code}</b> <span className="text-gray-500">{codeLabel(x.t.code)}</span></span>
        : <span className="text-gray-400">—</span>),
    },
    ...(docked ? [] : [
      { title: 'Ghi chú', key: 'note', ellipsis: true, render: (_: unknown, x: LogRow) => x.t.text || <span className="text-gray-400">—</span> },
      { title: 'Người gọi', key: 'by', width: 140, render: (_: unknown, x: LogRow) => x.t.by },
    ]),
    {
      title: 'Ghi âm', key: 'rec', width: 80,
      render: (_, x) => (x.t.dur && isConnected(x.t.code) ? <Tooltip title="Nghe ghi âm khi đã nối tổng đài"><Button size="small" type="text" disabled icon={<Play size={13} />}>Nghe</Button></Tooltip> : <span className="text-gray-400">—</span>),
    },
  ];

  const who = staffId ? teleStaffs.find((s) => s.value === staffId)?.label ?? me.name : canManage ? 'cả phòng Telesales' : me.name;
  const empty = !loading && !rows.length;

  return (
    <Layout>
      <Helmet><title>Tổng đài Sales | Khothi</title></Helmet>
      <div className="flex w-full items-start gap-4 text-sm">
        <div className="min-w-0 flex-1 space-y-3 pb-16">
          {/* Tiêu đề + trạng thái trực máy + thao tác chính */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex min-w-[240px] flex-1 items-center gap-2.5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#e8f4f7] text-[#3e91ab] dark:bg-[#3e91ab]/20"><Headset size={18} /></span>
              <div className="min-w-0">
                <h1 className="m-0 truncate text-[17px] font-bold leading-tight text-gray-900 dark:text-gray-50">Tổng đài Sales</h1>
                <div className="truncate text-[12px] text-gray-500 dark:text-gray-400">Hàng đợi gọi của {who} · việc quá hạn và cuộc nhỡ lên đầu</div>
              </div>
            </div>
            <div className="flex max-w-full flex-wrap items-center gap-2">
              <Segmented
                value={status}
                onChange={(v) => setStatus(v as typeof status)}
                options={[
                  { value: 'ready', label: <span className="flex items-center gap-1.5 px-0.5"><span className="h-2 w-2 rounded-full bg-emerald-500" />Sẵn sàng</span> },
                  { value: 'break', label: <span className="flex items-center gap-1.5 px-0.5"><Coffee size={13} />Tạm nghỉ</span> },
                ]}
              />
              {canManage && (
                <Select
                  allowClear
                  value={staffId}
                  onChange={(v) => { if (inCall) { message.warning('Đang có cuộc gọi chưa lưu kết quả.'); return; } setStaffId(v); setSelected(null); }}
                  placeholder="Tất cả Telesales"
                  options={teleStaffs}
                  showSearch
                  optionFilterProp="label"
                  className="w-[180px]"
                />
              )}
              <Tooltip title="Tra cứu, thêm khách, chia khách ở màn Khách tiềm năng">
                <Link to="/telesales/customer"><Button icon={<Contact size={15} />}>Khách tiềm năng</Button></Link>
              </Tooltip>
              <Tooltip title="Mô phỏng khách gọi vào tổng đài (cho tới khi nối tổng đài thật)">
                <Button icon={<PhoneIncoming size={15} />} onClick={simulateIncoming} disabled={!!ring}>Mô phỏng gọi đến</Button>
              </Tooltip>
              <Button type="primary" icon={<PhoneOutgoing size={15} />} onClick={callNext} disabled={!queue.some(callable) || inCall} className="!bg-[#3e91ab] font-semibold disabled:!bg-gray-200">
                Gọi khách tiếp theo
              </Button>
            </div>
          </div>

          {status === 'break' && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
              <Coffee size={15} /> Đang tạm nghỉ — cuộc gọi đến sẽ vào mục Cuộc nhỡ. Chuyển về <b>Sẵn sàng</b> để nhận cuộc gọi.
            </div>
          )}

          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
            <Kpi icon={<Phone size={16} />} label="Cuộc gọi hôm nay" value={kpi.total} sub={`Gọi đi ${kpi.out} · Gọi đến ${kpi.inbound} · Nhỡ ${kpi.missed}`} />
            <Kpi icon={<PhoneCall size={16} />} label="Tỉ lệ kết nối" value={kpi.rate === null ? '—' : `${kpi.rate}%`} sub={`${kpi.connected} / ${kpi.out} cuộc gọi đi`} tone="green" />
            <Kpi icon={<CalendarCheck2 size={16} />} label="Đặt lịch hôm nay" value={kpi.booked} sub={`Từ ${kpi.connected} cuộc kết nối`} tone="green" />
            <Kpi icon={<Clock3 size={16} />} label="Đàm thoại" value={talkText(kpi.talkSec)} sub="Tổng các cuộc hôm nay" tone="slate" />
            <Kpi
              icon={<AlarmClock size={16} />}
              label="Việc quá hạn"
              value={late}
              sub={`Cuộc nhỡ chưa gọi lại: ${missOpen}`}
              tone={late ? 'red' : 'amber'}
              active={tab === 'queue' && kind === 'late'}
              onClick={() => { setTab('queue'); setKind(kind === 'late' ? null : 'late'); }}
            />
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
            <Segmented
              value={tab}
              onChange={(v) => setTab(v as string)}
              options={[
                { value: 'queue', label: `Hàng đợi gọi (${queue.length})` },
                { value: 'missed', label: <span>Cuộc nhỡ{missOpen ? <span className="ml-1.5 rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">{missOpen}</span> : ''}</span> },
                { value: 'log', label: 'Nhật ký cuộc gọi' },
              ]}
              className="mb-3"
            />

            {empty ? (
              <Empty description={<span>Chưa có khách trong hàng đợi. <Link to="/telesales/customer" className="text-[#3e91ab]">Mở Khách tiềm năng</Link> để thêm khách hoặc tạo khách mẫu.</span>} />
            ) : tab === 'queue' ? (
              <>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {[{ k: null, label: 'Tất cả', n: queue.length }, { k: 'late' as const, label: 'Quá hạn', n: late }, ...QUEUE_KINDS.map((k) => ({ k, label: KIND_META[k].label, n: kindCount[k] }))].map((c) => {
                    const on = kind === c.k;
                    return (
                      <button
                        key={c.label}
                        type="button"
                        onClick={() => setKind(c.k)}
                        className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[12.5px] transition ${on ? 'border-[#3e91ab] bg-[#e8f4f7] font-semibold text-[#2c6b7f] dark:bg-[#3e91ab]/20 dark:text-[#8cc8da]' : 'border-gray-200 text-gray-600 hover:border-[#3e91ab] dark:border-gray-600 dark:text-gray-300'}`}
                      >
                        {c.label}<span className={`tabular-nums ${c.k === 'late' && c.n ? 'font-bold text-red-500' : 'text-gray-400'}`}>{c.n}</span>
                      </button>
                    );
                  })}
                </div>
                <Table
                  rowKey={(q) => q.r.id}
                  size="small"
                  loading={loading}
                  columns={queueCols}
                  dataSource={shown}
                  pagination={shown.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
                  rowClassName={(q) => (q.r.id === selected?.rec.id ? '!bg-[#e8f4f7] dark:!bg-[#3e91ab]/15' : q.late ? 'bg-red-50/40 dark:bg-red-500/5' : '')}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không còn việc trong mục này" /> }}
                  scroll={{ x: docked ? undefined : 900 }}
                />
              </>
            ) : tab === 'missed' ? (
              <>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <Segmented size="small" value={missFilter} onChange={(v) => setMissFilter(v as MissedFilter)} options={[{ value: 'open', label: `Chưa gọi lại (${missOpen})` }, { value: 'all', label: `7 ngày (${missRows.length})` }]} />
                  <span className="text-[12px] text-gray-400">Cuộc nhỡ phải gọi lại trong {MISSED_MIN} phút (R05)</span>
                </div>
                <Table
                  rowKey="key"
                  size="small"
                  columns={missCols}
                  dataSource={missFilter === 'open' ? missRows.filter((x) => !x.back) : missRows}
                  pagination={false}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có cuộc nhỡ cần gọi lại" /> }}
                />
              </>
            ) : (
              <>
                <div className="mb-2"><Segmented size="small" value={logDays} onChange={(v) => setLogDays(v as 1 | 7)} options={[{ value: 1, label: 'Hôm nay' }, { value: 7, label: '7 ngày' }]} /></div>
                <Table
                  rowKey="key"
                  size="small"
                  columns={logCols}
                  dataSource={logRows}
                  pagination={logRows.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
                  locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có cuộc gọi" /> }}
                  scroll={{ x: docked ? undefined : 900 }}
                />
              </>
            )}
          </div>
        </div>

        {selected && (
          <LeadDrawer
            key={`${selected.rec.id}-${selected.seq}`}
            record={rows.find((r) => r.id === selected.rec.id) ?? selected.rec}
            tags={tags}
            careStatuses={careStatuses}
            canApprove={canManage}
            readOnly={!canWrite}
            initial={selected.action}
            canNext={queue.length > 1}
            onNext={callNext}
            onClose={() => { setSelected(null); setInCall(false); }}
            onCallChange={setInCall}
            expanded={drawerWide}
            onExpandedChange={setDrawerWide}
          />
        )}
      </div>

      {ring && <IncomingCall key={ring.id} ring={ring} l={ring.r ? leads[ring.r.id] : undefined} tags={tags} now={now} onAnswer={answer} onMiss={miss} />}

      <CreateCustomerModal
        open={!!createFor}
        initialPhone={createFor?.phone}
        onClose={() => setCreateFor(null)}
        onSubmit={() => {
          if (createFor) doneUnknown(createFor.id, me.name);
          setCreateFor(null);
          message.success('Đã tạo hồ sơ khách từ số lạ');
        }}
      />
    </Layout>
  );
};

export default TeleCallCenter;
